/**
 * As conversas do cliente de uma oportunidade, com quando cada uma abriu,
 * fechou e reabriu (24/09/2026).
 *
 * ── Todas do cliente, e não só as desta oportunidade ─────────────────────
 *
 * Decidido pelo Gabriel: quem abre a oportunidade quer ver tudo o que se falou
 * com aquele cliente. As ligadas a ESTA oportunidade vêm marcadas.
 *
 * ── O que a história de cada conversa sabe, e desde quando ───────────────
 *
 * A abertura vem da própria conversa. Fechamentos e reaberturas vêm das linhas
 * de atividade (051, 052), que só existem desde 23/09/2026. Conversa anterior
 * a isso mostra a abertura e, se estiver resolvida, o último fechamento — sem
 * os intermediários, que nunca foram gravados.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

export const EventoDaConversaSchema = z
  .object({
    tipo: z.enum(['aberta', 'resolvida', 'reaberta']),
    quando: InstanteSchema,
    /** Quem fez, quando foi alguém da equipe. Nulo: o cliente, ou o sistema. */
    por: z.string().nullable(),
  })
  .strict();

export type EventoDaConversa = z.infer<typeof EventoDaConversaSchema>;

export const ConversaDaOportunidadeSchema = z
  .object({
    id: IdSchema,
    situacao: z.enum(['aberta', 'pendente', 'resolvida', 'em_espera']),
    canal: z.string().nullable(),
    /** O nome do número ou da conta por onde veio. */
    caixa: z.string().nullable(),
    ultima_mensagem_em: InstanteSchema.nullable(),
    /** Ligada a ESTA oportunidade, e não só ao cliente. */
    desta_oportunidade: z.boolean(),
    /** Do mais antigo ao mais recente, que é a ordem em que a conversa aconteceu. */
    eventos: z.array(EventoDaConversaSchema),
  })
  .strict();

export type ConversaDaOportunidade = z.infer<typeof ConversaDaOportunidadeSchema>;

export const ConversasDaOportunidadeSchema = z
  .object({
    /** Nulo: a oportunidade ainda não tem cliente reconhecido. */
    cliente: z.object({ id: IdSchema, nome: z.string().nullable() }).strict().nullable(),
    /** Da mais recente à mais antiga. */
    itens: z.array(ConversaDaOportunidadeSchema),
    /** Há mais conversas do que as listadas; o Cliente 360 mostra todas. */
    tem_mais: z.boolean(),
  })
  .strict();

export type ConversasDaOportunidade = z.infer<typeof ConversasDaOportunidadeSchema>;
